import { Reveal, Bubbles, WaveDivider } from "@/components/site/Motion";
import { Btn } from "@/components/site/Btn";
import { ArrowRight, Building2, Users, FileText, BookText } from "lucide-react";
import { IT } from "@/lib/content";
import { ART, WHITE_PAPERS } from "@/lib/assets";

export default function ITProjects() {
  const m = IT.migration;
  const p = IT.partnership;
  return (
    <>
      {/* HERO */}
      <section className="relative bg-gradient-to-b from-seagreen/20 to-cream pt-16 md:pt-24 pb-4 overflow-hidden" data-testid="it-hero">
        <Bubbles count={7} />
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-[1fr_0.9fr] gap-10 items-center relative">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">IT Projects</p>
            <h1 className="font-display font-extrabold text-5xl md:text-7xl text-navy leading-[0.95]">{IT.hero}</h1>
            <p className="font-body text-lg md:text-xl text-navy/70 mt-6 max-w-xl">{IT.heroSub}</p>
            <p className="font-display font-bold text-xl text-navy mt-6 bg-yellow inline-block px-4 py-2 rounded-xl border-2 border-navy shadow-brutal -rotate-1">{IT.flagshipTag}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={ART.dataMigration} alt="Legacy data being assessed, cleaned, validated and organised into a modern system" className="w-full rounded-[32px] border-2 border-navy shadow-brutal-lg bg-white" loading="eager" />
          </Reveal>
        </div>
        <WaveDivider color="#FDFBF7" className="mt-8" />
      </section>

      {/* DATA MIGRATION INTRO */}
      <section id="data-migration" className="py-16 md:py-24 scroll-mt-24" data-testid="it-migration">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy leading-[1.05]">{m.headline}</h2>
            <p className="font-body text-lg text-navy/75 mt-5 max-w-3xl">{m.body}</p>
          </Reveal>
        </div>
        {/* TWO PATHS */}
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6 md:gap-8 mt-12">
          {m.paths.map((path, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className={`h-full ${i === 0 ? "bg-sky/15 border-sky" : "bg-coral/15 border-coral"} border-2 rounded-3xl p-8`}>
                <span className="w-12 h-12 grid place-items-center rounded-full bg-white border-2 border-navy">{i === 0 ? <Building2 className="w-6 h-6" /> : <Users className="w-6 h-6" />}</span>
                <span className="block font-body text-xs font-bold uppercase tracking-[0.15em] text-navy/70 mt-4">{path.tag}</span>
                <h3 className="font-display font-extrabold text-2xl text-navy mt-2">{path.hook}</h3>
                <p className="font-body text-navy/75 mt-3">{path.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* DELIVERY JOURNEY */}
      <section className="py-16 md:py-24 bg-navy" data-testid="it-journey">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-cream">How a migration actually runs.</h2>
            <p className="font-body text-lg text-cream/70 mt-4 max-w-2xl">Two phases. First we think it through. Then we move the data carefully.</p>
          </Reveal>

          <div className="mt-12 grid lg:grid-cols-2 gap-8">
            {[{ label: "Phase 01 — Think", steps: IT.journey.think, dot: "bg-yellow" }, { label: "Phase 02 — Execute", steps: IT.journey.execute, dot: "bg-seagreen" }].map((phase, pi) => (
              <Reveal key={pi} delay={pi * 0.12}>
                <div className="bg-cream border-2 border-navy rounded-3xl p-6 md:p-8 shadow-brutal-sky h-full">
                  <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean">{phase.label}</p>
                  <div className="mt-5 space-y-4">
                    {phase.steps.map((s, si) => (
                      <div key={si} className="flex gap-4 items-start">
                        <span className={`shrink-0 w-8 h-8 grid place-items-center rounded-full ${phase.dot} border-2 border-navy font-display font-extrabold text-sm`}>{si + 1}</span>
                        <div>
                          <p className="font-display font-bold text-navy text-lg">{s.title}</p>
                          <p className="font-body text-navy/65 text-sm">{s.body}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORM PARTNERSHIP */}
      <section className="py-16 md:py-24" data-testid="it-partnership">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-4xl md:text-5xl text-navy max-w-3xl leading-[1.05]">{m.partnershipHeadline}</h2>
            <p className="font-body text-lg text-navy/75 mt-5 max-w-3xl">{m.partnershipBody}</p>
          </Reveal>

          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-6 items-stretch mt-12">
            <Reveal>
              <div className="bg-sky/15 border-2 border-navy rounded-3xl p-7 h-full">
                <p className="font-display font-bold text-xl text-navy">{p.left.title}</p>
                <ul className="mt-4 space-y-2">{p.left.items.map((it) => (<li key={it} className="font-body text-navy/75 flex gap-2"><span className="text-ocean">•</span>{it}</li>))}</ul>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="flex lg:flex-col items-center justify-center gap-3">
              <div className="bg-yellow border-2 border-navy rounded-2xl px-5 py-4 shadow-brutal text-center max-w-[200px]">
                <p className="font-display font-extrabold text-navy leading-tight">{p.centre}</p>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="bg-navy border-2 border-navy rounded-3xl p-7 h-full">
                <p className="font-display font-bold text-xl text-cream">{p.right.title}</p>
                <ul className="mt-4 space-y-2">{p.right.items.map((it) => (<li key={it} className="font-body text-cream/80 flex gap-2"><span className="text-yellow">•</span>{it}</li>))}</ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <p className="font-display font-bold text-xl md:text-2xl text-navy text-center mt-10">{p.line}</p>
          </Reveal>

          {/* white label + start with one */}
          <div className="grid md:grid-cols-2 gap-6 mt-12">
            <Reveal>
              <div className="bg-white border-2 border-navy rounded-3xl p-8 shadow-brutal h-full">
                <img src={ART.platformPartnership} alt="A branded platform in front, with the BleuSharks migration team working quietly behind it" className="w-full rounded-2xl border-2 border-navy mb-5" loading="lazy" />
                {m.whiteLabel.map((w, i) => (<p key={i} className={`font-body ${i === 0 ? "font-display font-bold text-xl text-navy" : "text-navy/70 mt-2"}`}>{w}</p>))}
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-seagreen/15 border-2 border-navy rounded-3xl p-8 h-full flex flex-col">
                <h3 className="font-display font-extrabold text-3xl text-navy">{p.start.headline}</h3>
                <p className="font-body text-navy/75 mt-3 flex-1">{p.start.body}</p>
                <p className="font-body text-navy/70 italic mt-5 border-l-4 border-yellow pl-4">{m.philosophy}</p>
                <div className="mt-6"><Btn to="/contact" variant="yellow" testid="it-partnership-cta">Discuss a Migration <ArrowRight className="w-5 h-5" /></Btn></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* OTHER CAPABILITIES */}
      <section className="py-16 md:py-24 bg-sky/10" data-testid="it-other">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Reveal>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-navy">{IT.other.headline}</h2>
            <p className="font-body text-lg text-navy/70 mt-3 max-w-2xl">{IT.other.body}</p>
          </Reveal>
          <div className="flex flex-wrap gap-3 mt-8">
            {IT.other.items.map((it, i) => (
              <Reveal key={it} delay={i * 0.03}>
                <span className="bg-white border-2 border-navy rounded-full px-5 py-2.5 font-body font-semibold text-navy shadow-brutal-sky">{it}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHITE PAPER + CTA */}
      <section className="relative bg-ocean" data-testid="it-whitepaper">
        <div className="bg-sky/10"><WaveDivider color="#0284C7" /></div>
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-[1fr_auto] gap-8 items-center">
          <Reveal>
            <div className="flex items-center gap-3 text-cream/80"><BookText className="w-6 h-6 text-yellow" /><span className="font-body text-sm font-bold uppercase tracking-[0.15em]">White paper</span></div>
            <h2 className="font-display font-extrabold text-3xl md:text-5xl text-cream mt-3">Beyond the Platform.</h2>
            <p className="font-body text-lg text-cream/85 mt-3 max-w-xl">The full thinking on migration as a customer experience, written for cloud, Customer Relationship Management, Enterprise Resource Planning and software providers.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Btn href={WHITE_PAPERS.platform} variant="yellow" testid="it-read-platform" className="whitespace-nowrap"><FileText className="w-5 h-5" /> Read the White Paper</Btn>
          </Reveal>
        </div>
      </section>
    </>
  );
}
